import {
  AnalysisSettings,
  ComponentDesignTimestamps,
  FeaturePatternTrussSettings,
  LoadSettings,
  MaterialSettings,
  MaterialSettingsPair,
  OrganizationCapabilities,
  OrganizationSettings,
  ProfileDefaults,
} from '@paragon/contract-library';

import { DesignService } from '../connections';

export function getModelAnalysisSettings(modelGuid: string): Promise<AnalysisSettings> {
  return DesignService.get(`layoutModels/${modelGuid}/analysisSettings`);
}

export function getModelAnalysisSettingsBulk(modelGuids: readonly string[]) {
  return DesignService.post<{ [modelGuid: string]: AnalysisSettings }>(`layoutModels/analysisSettings/bulkLoad`, {
    data: modelGuids,
  });
}

export function getModelLoadSettings(modelGuid: string): Promise<LoadSettings> {
  return DesignService.get(`layoutModels/${modelGuid}/loadSettings`);
}

export function getModelLoadSettingsBulk(modelGuids: readonly string[]) {
  return DesignService.post<{ [modelGuid: string]: LoadSettings }>(`layoutModels/loadSettings/bulkLoad`, {
    data: modelGuids,
  });
}

export function getModelMaterialSettings(modelGuid: string): Promise<MaterialSettingsPair> {
  return DesignService.get(`layoutModels/${modelGuid}/materialSettings`);
}

export function getModelFeaturePatternSettings(modelGuid: string): Promise<FeaturePatternTrussSettings> {
  return DesignService.get(`layoutModels/${modelGuid}/featurePatternSettings`);
}

export function getModelProfileDefaults(modelGuid: string): Promise<ProfileDefaults> {
  return DesignService.get(`layoutModels/${modelGuid}/profileDefaults`);
}

export function getModelProfileDefaultsBulk(modelGuids: readonly string[]) {
  return DesignService.post<{ [modelGuid: string]: ProfileDefaults }>(`layoutModels/profileDefaults/bulkLoad`, {
    data: modelGuids,
  });
}

export function postSetComponentAnalysisSettings(
  componentDesignGuid: string,
  analysisSettings: AnalysisSettings,
): Promise<ComponentDesignTimestamps> {
  return DesignService.post(`componentDesigns/${componentDesignGuid}/analysisSettings`, { data: analysisSettings });
}

export function postSetComponentLoadSettings(
  componentDesignGuid: string,
  loadSettings: LoadSettings,
): Promise<ComponentDesignTimestamps> {
  return DesignService.post(`componentDesigns/${componentDesignGuid}/loadSettings`, { data: loadSettings });
}

export function postSetComponentFeaturePatternMaterialSettings(
  componentDesignGuid: string,
  materialSettings: MaterialSettings,
): Promise<ComponentDesignTimestamps> {
  return DesignService.post(`componentDesigns/${componentDesignGuid}/featurePatternMaterialSettings`, {
    data: materialSettings,
  });
}

export function postSetComponentProfileMaterialSettings(
  componentDesignGuid: string,
  materialSettings: MaterialSettings,
): Promise<ComponentDesignTimestamps> {
  return DesignService.post(`componentDesigns/${componentDesignGuid}/profileMaterialSettings`, {
    data: materialSettings,
  });
}

export function postSetComponentProfileDefaults(
  componentDesignGuid: string,
  profileDefaults: ProfileDefaults,
): Promise<ComponentDesignTimestamps> {
  return DesignService.post(`componentDesigns/${componentDesignGuid}/profileDefaults`, { data: profileDefaults });
}

export function postSetModelAnalysisSettingsInProject(
  projectGuid: string,
  modelGuid: string,
  analysisSettings: AnalysisSettings,
  setForEntireHierarchy: boolean,
) {
  return DesignService.post<readonly ComponentDesignTimestamps[]>(
    `projects/${projectGuid}/layoutModels/${modelGuid}/analysisSettings`,
    {
      params: new URLSearchParams({ setForEntireHierarchy: setForEntireHierarchy.toString() }),
      data: analysisSettings,
    },
  );
}

export function postSetModelLoadSettingsInProject(
  projectGuid: string,
  modelGuid: string,
  loadSettings: LoadSettings,
  setForEntireHierarchy: boolean,
) {
  return DesignService.post<readonly ComponentDesignTimestamps[]>(
    `projects/${projectGuid}/layoutModels/${modelGuid}/loadSettings`,
    {
      params: new URLSearchParams({ setForEntireHierarchy: setForEntireHierarchy.toString() }),
      data: loadSettings,
    },
  );
}

export function postSetModelMaterialSettingsInProject(
  projectGuid: string,
  modelGuid: string,
  materialSettings: MaterialSettingsPair,
  setForEntireHierarchy: boolean,
) {
  return DesignService.post(`projects/${projectGuid}/layoutModels/${modelGuid}/materialSettings`, {
    params: new URLSearchParams({ setForEntireHierarchy: setForEntireHierarchy.toString() }),
    data: materialSettings,
  });
}

export function postSetModelFeaturePatternSettingsInProject(
  projectGuid: string,
  modelGuid: string,
  featurePatternSettings: FeaturePatternTrussSettings,
  setForEntireHierarchy: boolean,
) {
  return DesignService.post(`projects/${projectGuid}/layoutModels/${modelGuid}/featurePatternSettings`, {
    params: new URLSearchParams({ setForEntireHierarchy: setForEntireHierarchy.toString() }),
    data: featurePatternSettings,
  });
}

export function postSetModelProfileDefaultsInProject(
  projectGuid: string,
  modelGuid: string,
  profileDefaults: ProfileDefaults,
  setForEntireHierarchy: boolean,
) {
  return DesignService.post(`projects/${projectGuid}/layoutModels/${modelGuid}/profileDefaults`, {
    params: new URLSearchParams({ setForEntireHierarchy: setForEntireHierarchy.toString() }),
    data: profileDefaults,
  });
}

export function postBulkSetModelLoadSettingsInProject(
  projectGuid: string,
  loadSettingsDictionary: { readonly [modelGuid: string]: LoadSettings },
) {
  return DesignService.post<readonly ComponentDesignTimestamps[]>(`projects/${projectGuid}/bulkLoadSettings`, {
    data: loadSettingsDictionary,
  });
}

export function postBulkSetModelAnalysisSettingsInProject(
  projectGuid: string,
  analysisSettingsDictionary: { readonly [modelGuid: string]: AnalysisSettings },
) {
  return DesignService.post<readonly ComponentDesignTimestamps[]>(`projects/${projectGuid}/bulkAnalysisSettings`, {
    data: analysisSettingsDictionary,
  });
}

type PushDownSettingsTypes =
  | 'loadSettings'
  | 'analysisSettings'
  | 'profileMaterialSettings'
  | 'featurePatternMaterialSettings'
  | 'profileDefaults';

type PushDownSettingsObjects = LoadSettings | AnalysisSettings | MaterialSettings | ProfileDefaults;

export async function postPushDownSettings(
  projectGuid: string,
  modelGuid: string,
  settingsType: PushDownSettingsTypes,
  settings: PushDownSettingsObjects,
): Promise<ComponentDesignTimestamps[]> {
  return DesignService.post(`projects/${projectGuid}/layoutModels/${modelGuid}/${settingsType}/pushDown`, {
    data: settings,
  });
}

export function getOrganizationSettings() {
  return DesignService.get<OrganizationSettings>(`organizationSettings`).catch((error) => {
    if (error.status === 404) {
      return null;
    }
    throw error;
  });
}

export function getOrganizationCapabilities() {
  return DesignService.get<OrganizationCapabilities>('organizationCapabilities');
}

export function getAllOrganizationSettings() {
  return DesignService.get<OrganizationSettings[]>(`organizationSettingsRoot`);
}

export function upsertOrganizationSettings(settings: OrganizationSettings) {
  return DesignService.post<OrganizationSettings>('organizationSettings', { data: settings });
}
